'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { CREDIT_PACKAGES } from '@/lib/services/creditsService';
import AppLogo from '@/components/ui/AppLogo';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Feature {
  icon: string;
  title: string;
  description: string;
  accent: string;
  span?: string;
}

interface Step {
  number: string;
  title: string;
  description: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

// ─── Product Journey Data ─────────────────────────────────────────────────────
interface ProductStep {
  id: string;
  step: number;
  label: string;
  title: string;
  tagline: string;
  description: string;
  bullets: string[];
  cta: string;
  href: string;
  accent: string;
  bgGradient: string;
  iconBg: string;
  icon: string;
  badge: string;
}

const PRODUCT_JOURNEY: ProductStep[] = [
  {
    id: 'narrative',
    step: 1,
    label: 'Narrative Architect',
    title: 'Build Your Visa Story',
    tagline: 'Start here — craft the narrative that wins approval',
    description:
      'Before you practice interviews, you need a compelling story. Narrative Architect uses AI to help you articulate your academic purpose, home ties, and funding credibility into a coherent, officer-ready narrative.',
    bullets: [
      'AI-guided discovery of your strongest visa arguments',
      'Career path alignment with your chosen program',
      'Home ties and return intent framing',
      'Funding credibility scoring and gap detection',
    ],
    cta: 'Build My Narrative',
    href: '/narrative-architect',
    accent: '#7c3aed',
    bgGradient: 'from-violet-500/8 to-purple-500/5',
    iconBg: 'from-violet-500 to-purple-600',
    icon: '✍️',
    badge: 'Step 1 of 3',
  },
  {
    id: 'interview',
    step: 2,
    label: 'Interview Simulator',
    title: 'Practice Under Real Pressure',
    tagline: 'Simulate the embassy — before you walk in',
    description:
      'Face an AI visa officer that adapts to your answers in real time. The simulator probes your narrative for inconsistencies, tests your composure under pressure, and scores every response across four critical dimensions.',
    bullets: [
      'Adaptive AI officer mirrors real embassy dynamics',
      'Live scoring: Clarity, Confidence, Relevance, Home Ties',
      'Approval probability updated after each answer',
      'Suggested rewrites for weak responses',
    ],
    cta: 'Start a Mock Interview',
    href: '/interview-session',
    accent: '#1e3a5f',
    bgGradient: 'from-blue-500/8 to-indigo-500/5',
    iconBg: 'from-[#1e3a5f] to-[#2d5282]',
    icon: '🎙️',
    badge: 'Step 2 of 3',
  },
  {
    id: 'transcript',
    step: 3,
    label: 'Transcript Analyzer',
    title: 'Diagnose Every Session',
    tagline: 'Turn your interview transcripts into a roadmap',
    description:
      'After each practice session, the Transcript Analyzer dissects your full conversation. It identifies refusal triggers, scores answer quality, and generates a 30-day improvement plan targeting your weakest areas.',
    bullets: [
      'Refusal trigger detection across all answers',
      'Per-question scoring with officer verdict simulation',
      'Gap analysis: what you said vs. what officers want',
      'Personalized 30-day preparation roadmap',
    ],
    cta: 'Analyze a Transcript',
    href: '/transcript-analyzer',
    accent: '#0f766e',
    bgGradient: 'from-teal-500/8 to-emerald-500/5',
    iconBg: 'from-teal-600 to-emerald-600',
    icon: '📊',
    badge: 'Step 3 of 3',
  },
];

// ─── Data ─────────────────────────────────────────────────────────────────────
const FEATURES: Feature[] = [
  {
    icon: '✍️',
    title: 'Visa Narrative Architect™',
    description: 'Build your complete visa story before you ever step into an interview — career goals, home ties, funding credibility, and return intent, all structured into an officer-ready narrative.',
    accent: 'from-violet-500/10 to-purple-500/8',
    span: 'col-span-2',
  },
  {
    icon: '🎙️',
    title: 'Embassy Interview Simulator™',
    description: 'Face an adaptive AI visa officer that probes your narrative for inconsistencies, scores every answer in real time, and updates your approval probability after each response.',
    accent: 'from-blue-500/10 to-indigo-500/8',
  },
  {
    icon: '📊',
    title: 'Visa Transcript Analyzer™',
    description: 'Upload any interview transcript and receive an Embassy Readiness Score, refusal-risk detection, question-by-question officer interpretation, and a 30-day improvement roadmap.',
    accent: 'from-teal-500/10 to-emerald-500/8',
  },
  {
    icon: '🎯',
    title: 'Personalized Question Bank',
    description: 'Questions calibrated to your visa type, university, funding source, and home country — not generic templates that every applicant gets.',
    accent: 'from-sky-500/10 to-cyan-500/8',
  },
  {
    icon: '🏡',
    title: 'Home Ties & Return Intent Engine',
    description: 'The #1 reason F1 visas are refused. Our AI evaluates how convincingly you demonstrate ties to your home country and coaches you to close the gap.',
    accent: 'from-rose-500/10 to-pink-500/8',
  },
  {
    icon: '💰',
    title: 'Funding Narrative Builder',
    description: 'AI evaluates your financial documentation and sponsor credibility, flags potential officer concerns, and helps you frame your funding story with precision.',
    accent: 'from-amber-500/10 to-orange-500/8',
    span: 'col-span-2',
  },
  {
    icon: '🏛️',
    title: 'Embassy-Specific Coaching',
    description: "Coaching calibrated to specific U.S. embassy locations and their known interview patterns — so your preparation matches the room you'll actually walk into.",
    accent: 'from-slate-500/10 to-gray-500/8',
  },
];

const STEPS: Step[] = [
  { number: '01', title: 'Build Your Visa Narrative', description: 'Use the Narrative Architect™ to craft your career story, home ties, and funding credibility into a coherent, officer-ready narrative.' },
  { number: '02', title: 'Run Mock Interviews', description: 'Start a session with the Embassy Interview Simulator™ — choose your intensity level and face an adaptive AI visa officer.' },
  { number: '03', title: 'Analyze Your Transcripts', description: 'After each session, the Transcript Analyzer™ scores your answers, detects refusal triggers, and identifies your weakest areas.' },
  { number: '04', title: 'Target Weak Areas', description: 'Use your personalized 30-day roadmap to focus practice on the categories where you scored lowest — funding, home ties, or clarity.' },
  { number: '05', title: 'Walk In Embassy-Ready', description: 'Enter your real interview with a documented readiness score, a polished narrative, and the composure that only comes from real practice.' },
];

const FAQS: FAQItem[] = [
  {
    question: 'What is the Visa Narrative Architect™?',
    answer: 'The Narrative Architect is an AI-powered planning engine that helps you discover, build, and strengthen your visa story before interview preparation begins — covering career goals, home ties, funding narrative, and return intent. It produces a complete, structured narrative you can use to anchor every interview answer.',
  },
  {
    question: 'How does the Embassy Interview Simulator™ work?',
    answer: 'The Simulator puts you in a realistic visa interview with an AI officer trained on real consular behavior. It adapts to your answers in real time, probes inconsistencies, scores every response across four dimensions (Clarity, Confidence, Relevance, Home Ties), and updates your approval probability after each answer.',
  },
  {
    question: 'What does the Visa Interview Transcript Analyzer™ do?',
    answer: 'The Transcript Analyzer evaluates a full interview transcript — from a mock session or a real interview — and produces an Embassy Readiness Score, question-by-question officer interpretation, refusal-risk detection, and a personalized 30-day improvement plan.',
  },
  {
    question: 'Do I need a credit card to start?',
    answer: 'No. Every new account gets 2 free interview sessions — no credit card required. You only pay when you want more sessions or premium features.',
  },
  {
    question: 'Which visa types does Vurb support?',
    answer: 'The platform currently focuses on F1 Student Visa preparation with all three engines — Narrative Architect, Interview Simulator, and Transcript Analyzer — fully calibrated for F1 applicants.',
  },
  {
    question: 'Do credits expire?',
    answer: 'Never. Credits you purchase are yours to use at your own pace — no monthly resets, no expiry dates.',
  },
  {
    question: 'Can Vurb guarantee visa approval?',
    answer: 'No. Vurb is an educational coaching platform. Visa decisions are made solely by U.S. consular officers. We help you prepare — the rest is up to you.',
  },
  {
    question: 'Is my interview data private?',
    answer: 'Yes. Your transcripts, narratives, and personal data are stored securely and are never shared with third parties.',
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────
function NavBar({ onScrollTo }: { onScrollTo: (id: string) => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, loading } = useAuth();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const navLinks = [
    { label: 'Features', id: 'features' },
    { label: 'How It Works', id: 'how-it-works' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <div className="flex items-center gap-2.5 flex-shrink-0">
          <AppLogo size={30} />
          <span className="font-extrabold text-base text-[#0f172a] tracking-tight">
            Vurb
          </span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => onScrollTo(link.id)}
              className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          {!loading && (
            user ? (
              <Link
                href="/user-dashboard"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-sm font-semibold hover:bg-[#2d5282] transition-colors"
              >
                Go to Dashboard →
              </Link>
            ) : (
              <>
                <Link
                  href="/sign-up-login"
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  Login
                </Link>
                <Link
                  href="/sign-up-login?tab=signup"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#6366f1] text-white text-sm font-semibold hover:bg-[#4f46e5] transition-colors shadow-sm"
                >
                  Get Started
                </Link>
              </>
            )
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <div className="w-5 h-4 flex flex-col justify-between">
            <span className={`block h-0.5 bg-slate-700 transition-transform duration-200 ${mobileOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
            <span className={`block h-0.5 bg-slate-700 transition-opacity duration-200 ${mobileOpen ? 'opacity-0' : ''}`} />
            <span className={`block h-0.5 bg-slate-700 transition-transform duration-200 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </div>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-b border-slate-200 px-4 pb-4 pt-2 flex flex-col gap-1">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => { onScrollTo(link.id); setMobileOpen(false); }}
              className="w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
            >
              {link.label}
            </button>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            {!loading && !user && (
              <>
                <Link href="/sign-up-login" className="w-full text-center px-4 py-3 text-sm font-medium text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
                  Login
                </Link>
                <Link href="/sign-up-login?tab=signup" className="w-full text-center px-4 py-3 text-sm font-semibold text-white bg-[#6366f1] rounded-xl hover:bg-[#4f46e5] transition-colors">
                  Get Started Free
                </Link>
              </>
            )}
            {!loading && user && (
              <Link href="/user-dashboard" className="w-full text-center px-4 py-3 text-sm font-semibold text-white bg-[#1e3a5f] rounded-xl hover:bg-[#2d5282] transition-colors">
                Go to Dashboard →
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function HeroSection({ onScrollTo }: { onScrollTo: (id: string) => void }) {
  const { user, loading } = useAuth();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#f8fafc] via-[#eef2ff] to-[#f0f9ff] pt-16">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#6366f1]/8 to-[#8b5cf6]/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-gradient-to-tr from-[#1e3a5f]/6 to-[#3b82f6]/4 blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-gradient-to-r from-[#6366f1]/3 to-transparent blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 text-center py-20">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#e0e7ff] shadow-sm text-xs font-semibold text-[#6366f1] mb-8">
          <span className="w-2 h-2 rounded-full bg-[#6366f1] animate-pulse" />
          Three-Engine Visa Preparation Platform
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-[#0f172a] tracking-tight leading-[1.08] mb-6">
          Master Your{' '}
          <span className="relative inline-block">
            <span className="relative z-10 bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] bg-clip-text text-transparent">
              U.S. Visa Interview
            </span>
            <span className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-[#6366f1]/30 to-[#8b5cf6]/30 rounded-full" />
          </span>
          {' '}With AI
        </h1>

        {/* Subheadline — updated to reflect three engines */}
        <p className="text-lg sm:text-xl text-slate-500 max-w-2xl mx-auto leading-relaxed mb-6">
          Build your visa narrative, simulate embassy interviews, and analyze your transcripts — three AI engines working together to get you approved.
        </p>

        {/* Three engine pills */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          {[
            { icon: '✍️', label: 'Narrative Architect™', color: '#7c3aed' },
            { icon: '🎙️', label: 'Interview Simulator™', color: '#1e3a5f' },
            { icon: '📊', label: 'Transcript Analyzer™', color: '#0f766e' },
          ].map((engine) => (
            <span
              key={engine.label}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border"
              style={{
                background: `${engine.color}10`,
                borderColor: `${engine.color}30`,
                color: engine.color,
              }}
            >
              <span>{engine.icon}</span>
              {engine.label}
            </span>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          {!loading && (
            user ? (
              <Link
                href="/user-dashboard"
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-[#1e3a5f] text-white text-base font-bold hover:bg-[#2d5282] transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              >
                Go to Dashboard →
              </Link>
            ) : (
              <>
                <Link
                  href="/sign-up-login?tab=signup"
                  className="inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-[#6366f1] text-white text-base font-bold hover:bg-[#4f46e5] transition-all shadow-lg shadow-indigo-200 hover:shadow-xl hover:shadow-indigo-200 hover:-translate-y-0.5 active:translate-y-0"
                >
                  Start Free — 2 Sessions Included
                </Link>
                <button
                  onClick={() => onScrollTo('pricing')}
                  className="inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-white text-[#1e3a5f] text-base font-bold border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all shadow-sm hover:-translate-y-0.5 active:translate-y-0"
                >
                  View Pricing
                </button>
              </>
            )
          )}
        </div>

        {/* Social proof strip */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-slate-400">
          <div className="flex items-center gap-2">
            <span className="text-[#10b981] font-bold text-base">✓</span>
            <span>No credit card required</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-slate-300 hidden sm:block" />
          <div className="flex items-center gap-2">
            <span className="text-[#10b981] font-bold text-base">✓</span>
            <span>2 free sessions for new users</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-slate-300 hidden sm:block" />
          <div className="flex items-center gap-2">
            <span className="text-[#10b981] font-bold text-base">✓</span>
            <span>F1 Student Visa focused</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductJourneyTour() {
  const [activeStep, setActiveStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAutoPlay = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setActiveStep((s) => (s + 1) % PRODUCT_JOURNEY.length);
        setAnimating(false);
      }, 220);
    }, 4500);
  };

  useEffect(() => {
    if (autoPlay) startAutoPlay();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay]);

  const goTo = (i: number) => {
    if (animating || i === activeStep) return;
    setAutoPlay(false);
    if (intervalRef.current) clearInterval(intervalRef.current);
    setAnimating(true);
    setTimeout(() => {
      setActiveStep(i);
      setAnimating(false);
    }, 220);
  };

  const product = PRODUCT_JOURNEY[activeStep];

  return (
    <section id="product-journey" className="py-24 bg-gradient-to-b from-white to-[#f8fafc] overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block px-3 py-1 rounded-full bg-[#eef2ff] text-[#6366f1] text-xs font-bold uppercase tracking-widest mb-4">
            The Three-Product Journey
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight mb-4">
            From story to approval — in three steps
          </h2>
          <p className="text-slate-500 text-base max-w-2xl mx-auto leading-relaxed">
            Vurb is built around a proven preparation sequence. Each product feeds into the next, creating a complete system for visa interview success.
          </p>
        </div>

        {/* Step selector tabs */}
        <div className="flex items-stretch gap-3 mb-10 overflow-x-auto pb-1 scrollbar-hide">
          {PRODUCT_JOURNEY.map((p, i) => (
            <button
              key={p.id}
              onClick={() => goTo(i)}
              className={`flex-1 min-w-[160px] relative rounded-2xl px-5 py-4 text-left transition-all duration-300 border-2 group ${
                i === activeStep
                  ? 'border-transparent shadow-lg scale-[1.02]'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
              }`}
              style={i === activeStep ? {
                background: `linear-gradient(135deg, ${p.accent}12, ${p.accent}06)`,
                borderColor: `${p.accent}40`,
              } : {}}
            >
              {/* Step number */}
              <div
                className="w-7 h-7 rounded-xl flex items-center justify-center text-white text-xs font-black mb-3 transition-all"
                style={{ background: i === activeStep ? `linear-gradient(135deg, ${p.accent}, ${p.accent}cc)` : undefined }}
              >
                {i === activeStep ? (
                  <span>{p.step}</span>
                ) : (
                  <span className="text-slate-400 font-black text-xs w-7 h-7 rounded-xl bg-slate-100 flex items-center justify-center">{p.step}</span>
                )}
              </div>
              <p className={`text-xs font-bold uppercase tracking-wider mb-0.5 ${i === activeStep ? 'text-slate-700' : 'text-slate-400'}`}>
                {p.label}
              </p>
              <p className={`text-[11px] leading-snug ${i === activeStep ? 'text-slate-500' : 'text-slate-400'}`}>
                {p.tagline}
              </p>

              {/* Active indicator bar */}
              {i === activeStep && (
                <div
                  className="absolute bottom-0 left-4 right-4 h-0.5 rounded-full"
                  style={{ background: `linear-gradient(90deg, ${p.accent}, ${p.accent}60)` }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Flow connector */}
        <div className="hidden sm:flex items-center justify-center gap-2 mb-10">
          {PRODUCT_JOURNEY.map((p, i) => (
            <React.Fragment key={p.id}>
              <div
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-300"
                style={i === activeStep ? {
                  background: `${p.accent}15`,
                  color: p.accent,
                } : { background: '#f1f5f9', color: '#94a3b8' }}
              >
                <span>{p.icon}</span>
                <span>{p.label}</span>
              </div>
              {i < PRODUCT_JOURNEY.length - 1 && (
                <svg width="20" height="12" viewBox="0 0 20 12" fill="none" className="flex-shrink-0">
                  <path d="M1 6h16M13 1l6 5-6 5" stroke={i < activeStep ? '#6366f1' : '#cbd5e1'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Main content card */}
        <div
          className="relative rounded-3xl overflow-hidden border border-slate-100 shadow-xl transition-all duration-300"
          style={{
            opacity: animating ? 0 : 1,
            transform: animating ? 'translateY(10px)' : 'translateY(0)',
            transition: 'opacity 0.22s ease, transform 0.22s ease',
          }}
        >
          {/* Background gradient */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(135deg, ${product.accent}08, ${product.accent}03, transparent)` }}
          />

          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* Left: Content */}
            <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-between">
              {/* Badge */}
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl shadow-md"
                  style={{ background: `linear-gradient(135deg, ${product.accent}, ${product.accent}cc)` }}
                >
                  {product.icon}
                </div>
                <div>
                  <span
                    className="inline-block px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest mb-1"
                    style={{ background: `${product.accent}15`, color: product.accent }}
                  >
                    {product.badge}
                  </span>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">{product.label}</p>
                </div>
              </div>

              <div className="flex-1">
                <h3 className="text-2xl sm:text-3xl font-extrabold text-[#0f172a] tracking-tight mb-2">
                  {product.title}
                </h3>
                <p className="text-sm font-semibold mb-5" style={{ color: product.accent }}>
                  {product.tagline}
                </p>
                <p className="text-sm text-slate-500 leading-relaxed mb-7">
                  {product.description}
                </p>

                {/* Bullets */}
                <ul className="flex flex-col gap-3 mb-8">
                  {product.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: `${product.accent}18` }}
                      >
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4l2.5 2.5L9 1" stroke={product.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <span className="text-sm text-slate-600 leading-snug">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="flex items-center gap-4 flex-wrap">
                <Link
                  href={product.href}
                  className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl text-white text-sm font-bold transition-all hover:-translate-y-0.5 active:translate-y-0 shadow-md hover:shadow-lg"
                  style={{ background: `linear-gradient(135deg, ${product.accent}, ${product.accent}cc)` }}
                >
                  {product.cta} →
                </Link>
                {activeStep < PRODUCT_JOURNEY.length - 1 && (
                  <button
                    onClick={() => goTo(activeStep + 1)}
                    className="text-sm text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-1.5 font-medium"
                  >
                    Next: {PRODUCT_JOURNEY[activeStep + 1].label}
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M3 7h8M7 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            {/* Right: Visual panel */}
            <div
              className="relative hidden lg:flex flex-col items-center justify-center p-10 min-h-[420px]"
              style={{ background: `linear-gradient(135deg, ${product.accent}10, ${product.accent}05)` }}
            >
              {/* Decorative rings */}
              <div
                className="absolute inset-0 pointer-events-none overflow-hidden"
              >
                <div
                  className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20"
                  style={{ background: `radial-gradient(circle, ${product.accent}40, transparent 70%)` }}
                />
                <div
                  className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full opacity-15"
                  style={{ background: `radial-gradient(circle, ${product.accent}30, transparent 70%)` }}
                />
              </div>

              {/* Central icon */}
              <div className="relative flex flex-col items-center gap-6">
                <div
                  className="w-24 h-24 rounded-3xl flex items-center justify-center text-5xl shadow-2xl"
                  style={{ background: `linear-gradient(135deg, ${product.accent}, ${product.accent}bb)` }}
                >
                  {product.icon}
                </div>

                {/* Step progress dots */}
                <div className="flex items-center gap-3">
                  {PRODUCT_JOURNEY.map((p, i) => (
                    <button
                      key={p.id}
                      onClick={() => goTo(i)}
                      className="rounded-full transition-all duration-300"
                      style={{
                        width: i === activeStep ? 28 : 8,
                        height: 8,
                        background: i === activeStep ? product.accent : i < activeStep ? `${product.accent}50` : '#e2e8f0',
                      }}
                      aria-label={`Go to ${p.label}`}
                    />
                  ))}
                </div>

                {/* Mini stat cards */}
                <div className="grid grid-cols-2 gap-3 w-full max-w-xs">
                  {[
                    { label: 'Narrative Score', value: '94%', sub: 'Credibility' },
                    { label: 'Mock Sessions', value: '12', sub: 'Completed' },
                    { label: 'Approval Prob.', value: '87%', sub: 'Readiness' },
                    { label: 'Refusal Risks', value: '2', sub: 'Identified' },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      className="rounded-2xl p-3.5 bg-white/70 backdrop-blur-sm border border-white/80 shadow-sm"
                    >
                      <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mb-0.5">{stat.label}</p>
                      <p className="text-xl font-extrabold" style={{ color: product.accent }}>{stat.value}</p>
                      <p className="text-[10px] text-slate-400">{stat.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA strip */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <p className="text-sm text-slate-500">
            All three tools work together — your narrative informs your practice, your practice generates transcripts, your transcripts reveal what to fix.
          </p>
          <Link
            href="/sign-up-login?tab=signup"
            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#6366f1] text-white text-sm font-bold hover:bg-[#4f46e5] transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 active:translate-y-0"
          >
            Start the Journey →
          </Link>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  return (
    <section id="features" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-[#eef2ff] text-[#6366f1] text-xs font-bold uppercase tracking-widest mb-4">
            Platform Engines
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight mb-4">
            Three engines. One complete preparation system.
          </h2>
          <p className="text-slate-500 text-base max-w-xl mx-auto">
            Each engine is purpose-built for a different phase of your visa preparation — and they work together as a complete system.
          </p>
        </div>

        {/* Bento grid — varied sizes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Large card — Narrative Architect */}
          <div className={`lg:col-span-2 relative rounded-3xl p-7 bg-gradient-to-br ${FEATURES[0].accent} border border-slate-100 overflow-hidden group hover:shadow-lg transition-shadow`}>
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-gradient-to-br from-violet-400/10 to-purple-400/10 blur-2xl" />
            <div className="text-4xl mb-4">{FEATURES[0].icon}</div>
            <h3 className="text-lg font-bold text-[#0f172a] mb-2">{FEATURES[0].title}</h3>
            <p className="text-sm text-slate-500 leading-relaxed">{FEATURES[0].description}</p>
          </div>

          {/* Regular cards */}
          {FEATURES.slice(1, 5).map((feature) => (
            <div
              key={feature.title}
              className={`relative rounded-3xl p-6 bg-gradient-to-br ${feature.accent} border border-slate-100 overflow-hidden group hover:shadow-lg transition-shadow`}
            >
              <div className="text-3xl mb-3">{feature.icon}</div>
              <h3 className="text-sm font-bold text-[#0f172a] mb-1.5">{feature.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{feature.description}</p>
            </div>
          ))}

          {/* Large card — Funding Narrative Builder */}
          <div className={`lg:col-span-2 relative rounded-3xl p-7 bg-gradient-to-br ${FEATURES[5].accent} border border-slate-100 overflow-hidden group hover:shadow-lg transition-shadow`}>
            <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full bg-gradient-to-tr from-amber-400/10 to-orange-400/10 blur-2xl" />
            <div className="text-4xl mb-4">{FEATURES[5].icon}</div>
            <h3 className="text-lg font-bold text-[#0f172a] mb-2">{FEATURES[5].title}</h3>
            <p className="text-sm text-slate-500 leading-relaxed">{FEATURES[5].description}</p>
          </div>

          {/* Embassy card */}
          <div className={`lg:col-span-2 relative rounded-3xl p-6 bg-gradient-to-br ${FEATURES[6].accent} border border-slate-100 overflow-hidden group hover:shadow-lg transition-shadow`}>
            <div className="text-3xl mb-3">{FEATURES[6].icon}</div>
            <h3 className="text-sm font-bold text-[#0f172a] mb-1.5">{FEATURES[6].title}</h3>
            <p className="text-xs text-slate-500 leading-relaxed">{FEATURES[6].description}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  return (
    <section id="how-it-works" className="py-24 bg-gradient-to-b from-[#f8fafc] to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-[#eef2ff] text-[#6366f1] text-xs font-bold uppercase tracking-widest mb-4">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight mb-4">
            From narrative to embassy-ready in 5 steps
          </h2>
        </div>

        {/* Horizontal step cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STEPS.map((step, i) => (
            <div
              key={step.number}
              className="relative bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow group"
            >
              {/* Connector line */}
              {i < STEPS.length - 1 && (
                <div className="hidden lg:block absolute top-8 -right-2 w-4 h-0.5 bg-gradient-to-r from-slate-200 to-slate-100 z-10" />
              )}
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] flex items-center justify-center mb-4 shadow-sm group-hover:shadow-md transition-shadow">
                <span className="text-white text-xs font-black">{step.number}</span>
              </div>
              <h3 className="text-sm font-bold text-[#0f172a] mb-2">{step.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PricingSection() {
  const { user } = useAuth();
  const router = useRouter();
  const [loadingPackage, setLoadingPackage] = useState<string | null>(null);

  async function handlePurchase(packageId: string) {
    if (!user) {
      router.push('/sign-up-login');
      return;
    }
    setLoadingPackage(packageId);
    try {
      const res = await fetch('/api/payments/initialize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packageId, userId: user.id, userEmail: user.email }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Payment initialization failed');
      if (data.authorizationUrl) window.location.href = data.authorizationUrl;
    } catch {
      // silent — user stays on page
    } finally {
      setLoadingPackage(null);
    }
  }

  return (
    <section id="pricing" className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-[#eef2ff] text-[#6366f1] text-xs font-bold uppercase tracking-widest mb-4">
            Pricing
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight mb-4">
            Pay for sessions, not subscriptions
          </h2>
          <p className="text-slate-500 text-base max-w-xl mx-auto">
            Buy credits once, use them anytime. Credits never expire.
          </p>
        </div>

        {/* Free tier banner */}
        <div className="flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-r from-[#f0fdf4] to-[#ecfdf5] border border-[#a7f3d0] mb-10">
          <div className="w-10 h-10 rounded-xl bg-[#d1fae5] flex items-center justify-center flex-shrink-0 text-xl">🎁</div>
          <div>
            <p className="text-sm font-bold text-[#065f46]">2 Free Sessions for Every New User</p>
            <p className="text-xs text-[#047857] mt-0.5">No credit card required. Start practicing immediately after signing up.</p>
          </div>
          {!user && (
            <Link href="/sign-up-login?tab=signup" className="ml-auto flex-shrink-0 px-4 py-2 rounded-xl bg-[#10b981] text-white text-sm font-semibold hover:bg-[#059669] transition-colors">
              Sign up free
            </Link>
          )}
        </div>

        {/* Packages grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {CREDIT_PACKAGES.map((pkg) => (
            <div
              key={pkg.id}
              className={`relative flex flex-col rounded-2xl overflow-hidden transition-all hover:-translate-y-1 ${
                pkg.popular
                  ? 'border-2 border-[#6366f1] shadow-xl shadow-indigo-100'
                  : 'border border-slate-200 shadow-sm hover:shadow-md'
              }`}
            >
              {pkg.popular && (
                <div className="bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] text-white text-[10px] font-black text-center py-1.5 tracking-widest uppercase">
                  ✦ Best Value
                </div>
              )}
              <div className="p-5 flex flex-col gap-4 flex-1 bg-white">
                <div>
                  <p className={`text-[10px] font-black uppercase tracking-widest mb-2 ${pkg.popular ? 'text-[#6366f1]' : 'text-slate-400'}`}>
                    {pkg.name}
                  </p>
                  <div className="flex items-end gap-1 mb-1">
                    <span className="text-2xl font-extrabold text-[#0f172a]">${pkg.price}</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    <span className="font-bold text-[#0f172a]">{pkg.sessions}</span> sessions
                  </p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    ${(pkg.price / pkg.sessions).toFixed(2)}/session
                  </p>
                </div>
                <button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={loadingPackage === pkg.id}
                  className={`w-full text-xs font-bold py-2.5 px-3 rounded-xl transition-all flex items-center justify-center gap-1.5 disabled:opacity-60 ${
                    pkg.popular
                      ? 'bg-[#6366f1] text-white hover:bg-[#4f46e5]'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {loadingPackage === pkg.id ? (
                    <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    `Buy ${pkg.sessions} Sessions`
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-24 bg-gradient-to-b from-[#f8fafc] to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-16">
          <span className="inline-block px-3 py-1 rounded-full bg-[#eef2ff] text-[#6366f1] text-xs font-bold uppercase tracking-widest mb-4">
            FAQ
          </span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0f172a] tracking-tight">
            Common questions
          </h2>
        </div>

        <div className="flex flex-col gap-3">
          {FAQS.map((faq, i) => (
            <div
              key={i}
              className={`rounded-2xl border overflow-hidden transition-all ${
                openIndex === i ? 'border-[#c7d2fe] shadow-sm' : 'border-slate-200'
              }`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left bg-white hover:bg-slate-50 transition-colors"
              >
                <span className="text-sm font-semibold text-[#0f172a]">{faq.question}</span>
                <span
                  className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                    openIndex === i ? 'bg-[#6366f1] rotate-45' : 'bg-slate-100'
                  }`}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M6 2v8M2 6h8" stroke={openIndex === i ? '#fff' : '#64748b'} strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </span>
              </button>
              {openIndex === i && (
                <div className="px-6 pb-5 pt-1 bg-white">
                  <p className="text-sm text-slate-500 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { user, loading } = useAuth();

  return (
    <section className="py-24 bg-gradient-to-br from-[#1e3a5f] via-[#2d5282] to-[#1e40af] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#6366f1]/15 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-[#3b82f6]/10 blur-3xl" />
      </div>
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-4">
          Your visa interview is closer than you think
        </h2>
        <p className="text-blue-200 text-base mb-10 max-w-xl mx-auto leading-relaxed">
          Start with 2 free sessions. No credit card. No commitment. Just better preparation.
        </p>
        {!loading && (
          user ? (
            <Link
              href="/user-dashboard"
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-white text-[#1e3a5f] text-base font-bold hover:bg-slate-100 transition-all shadow-lg hover:-translate-y-0.5 active:translate-y-0"
            >
              Go to Dashboard →
            </Link>
          ) : (
            <Link
              href="/sign-up-login?tab=signup"
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-[#6366f1] text-white text-base font-bold hover:bg-[#4f46e5] transition-all shadow-lg shadow-indigo-900/30 hover:-translate-y-0.5 active:translate-y-0"
            >
              Start Free — 2 Sessions Included
            </Link>
          )
        )}
      </div>
    </section>
  );
}

function Footer({ onScrollTo }: { onScrollTo: (id: string) => void }) {
  return (
    <footer className="bg-[#0f172a] text-slate-400 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <AppLogo size={26} />
            <span className="font-extrabold text-sm text-white tracking-tight">
              Vurb
            </span>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
            {[
              { label: 'Features', id: 'features' },
              { label: 'How It Works', id: 'how-it-works' },
              { label: 'Pricing', id: 'pricing' },
              { label: 'FAQ', id: 'faq' },
            ].map((link) => (
              <button
                key={link.id}
                onClick={() => onScrollTo(link.id)}
                className="hover:text-white transition-colors"
              >
                {link.label}
              </button>
            ))}
            <Link href="/sign-up-login" className="hover:text-white transition-colors">Login</Link>
            <Link href="/legal/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link href="/legal/privacy" className="hover:text-white transition-colors">Privacy</Link>
          </nav>
        </div>
        <div className="mt-8 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-slate-600">
          <span>© 2026 Vurb. Powered by Twiddle.</span>
          <span className="text-slate-700">Educational coaching platform. Not affiliated with the U.S. Department of State.</span>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const { user, loading } = useAuth();
  const router = useRouter();
  const hasRedirected = useRef(false);

  // Redirect authenticated users to dashboard
  useEffect(() => {
    if (!loading && user && !hasRedirected.current) {
      hasRedirected.current = true;
      router.replace('/user-dashboard');
    }
  }, [user, loading, router]);

  function scrollTo(id: string) {
    const el = document.getElementById(id);
    if (el) {
      const offset = 72; // navbar height
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }

  // Show nothing while checking auth (prevents flash)
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="w-8 h-8 border-2 border-[#6366f1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If authenticated, show nothing while redirecting
  if (user) return null;

  return (
    <div className="min-h-screen bg-white">
      <NavBar onScrollTo={scrollTo} />
      <HeroSection onScrollTo={scrollTo} />
      <ProductJourneyTour />
      <FeaturesSection />
      <HowItWorksSection />
      <PricingSection />
      <FAQSection />
      <CTASection />
      <Footer onScrollTo={scrollTo} />
    </div>
  );
}
